import { useRef, useState } from 'react'
import { Input, Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconFolderPlus } from '@tabler/icons-react'
import ProjectTemplateSelect, { useProjectTemplates } from '@/components/portal/ProjectTemplateSelect.tsx'

interface CreateProjectModalProps {
  modalRef: React.RefObject<HTMLDialogElement>
  onConfirm: (title: string, templateId: string | null) => void
  isLoading?: boolean
}

export function useCreateProjectModal() {
  const modalRef = useRef<HTMLDialogElement>(null)
  const show = () => modalRef.current?.showModal()
  const close = () => modalRef.current?.close()
  return { modalRef, show, close }
}

export function CreateProjectModal({ modalRef, onConfirm, isLoading }: CreateProjectModalProps) {
  const [title, setTitle] = useState('')
  const [templateId, setTemplateId] = useState<string | null>(null)
  const { data: templates } = useProjectTemplates()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    onConfirm(title.trim(), templateId)
  }

  return (
    <Modal ref={modalRef} className="bg-neutral">
      <Modal.Header>
        <div className="flex flex-col items-center gap-3 pt-2">
          <div className="bg-primary bg-opacity-20 text-primary rounded-full p-4">
            <IconFolderPlus size={36} />
          </div>
          <span className="text-xl font-bold text-center">Neues Projekt erstellen</span>
        </div>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="form-control w-full">
            <div className="label">
              <span className="label-text">Projektname</span>
            </div>
            <Input
              placeholder="z.B. Hochzeit Schmidt"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              autoFocus
            />
          </label>
          {/* Only worth showing once the photographer actually has templates */}
          {!!templates?.length && (
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Vorlage</span>
              </div>
              <ProjectTemplateSelect value={templateId} onChange={setTemplateId} disabled={isLoading} />
              <div className="label">
                <span className="label-text-alt opacity-60">
                  Die Einstellungen der Vorlage werden direkt auf das neue Projekt übertragen.
                </span>
              </div>
            </label>
          )}
          <div className="flex flex-row gap-3 justify-center">
            <form method="dialog">
              <Button type="button" color="ghost" disabled={isLoading}
                onClick={() => modalRef.current?.close()}>
                Abbrechen
              </Button>
            </form>
            <Button type="submit" color="primary" loading={isLoading} disabled={isLoading || !title.trim()}>
              Erstellen
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}
